import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Play, Sparkle, FilmStrip, Hash, Users, ChatCircle, BookmarkSimple, Flame, Trophy, MusicNotes, Shuffle } from "@phosphor-icons/react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useBgMusic } from "../context/BackgroundMusicContext";
import DailyPlan from "../components/DailyPlan";
import DailyDevotionalSubscribe from "../components/DailyDevotionalSubscribe";
import DailyDevotionalCard from "../components/DailyDevotionalCard";
import TicketsCollection from "../components/TicketsCollection";
import TodayTrioCards from "../components/TodayTrioCards";
import PlayAllTodayButton from "../components/PlayAllTodayButton";
import WeeklyRhythmCard from "../components/WeeklyRhythmCard";
import WeekInWorshipCard from "../components/WeekInWorshipCard";
// PushOptInCard import removed 2026-06-30 (web push disabled, email only).
import LateNightComedyRail from "../components/LateNightComedyRail";
import ContinueWatchingRail from "../components/ContinueWatchingRail";
import MostWatchedRail from "../components/MostWatchedRail";
import SundayServiceBanner from "../components/SundayServiceBanner";
import ChannelSuggestionsRail from "../components/ChannelSuggestionsRail";
import ChannelTrendingRail from "../components/ChannelTrendingRail";
import MediaThumb from "../components/MediaThumb";
import { LiveNowRail } from "../components/Live";
import BirthdayRail from "../components/BirthdayRail";
import TonightPrayer from "../components/TonightPrayer";
import WhatsNewTour from "../components/WhatsNewTour";
import TodayBanner from "../components/TodayBanner";
import HandpickedTonightRail from "../components/HandpickedTonightRail";
import SundayDinnerRail from "../components/SundayDinnerRail";
import OwnerLlmHealthBanner from "../components/OwnerLlmHealthBanner";
import Tr from "../components/Tr";
import ReadAloudButton from "../components/ReadAloudButton";
import SiteFooter from "../components/SiteFooter";
import CrossDeviceBanner from "../components/CrossDeviceBanner";
import ResumeListeningChip from "../components/ResumeListeningChip";

// Spoken version of the hero bio. MUST stay word-for-word identical
// to the two <Tr> paragraphs rendered below in the hero — if you
// edit one, edit the other. Only deviation: the standalone H1
// tagline ("Faith, but make it social.") is prepended because it
// sits directly above the bio and reads naturally as the opening
// line when the audio kicks off.
const HERO_BIO_SPOKEN = [
  "Faith, but make it social.",
  "Holy Words started with one question: why is there no Bible app that actually built like the rest of your phone? You can post on TikTok but you can't post a 30 second testimony to your church family. You can DM your friends but you can't open a Discord-style study rooms with them and break down a chapter line by line. You can stream a billion artist but the Gospel and faith based rap you love is buried 4 playlists deep. We built the home that all of that should have had from day one.",
  "Holy Words is the first-faith social Bible app that brings Scripture to life in 18 languages with read-aloud studio voices, while doubling as the home for everything your spiritual life needs. Post videos and shorts, hop into Discord-style study rooms with friends and family, queue up gospel, faith based rap, and worship music, watch Black American faith films and foundational sermons, give the kids their own animated Bible cartoons and study group, catch the day's Live Newsroom, share Sunday Dinner recipes with the family, sharpen up on Scripture with 162 Biblical crossword and word search puzzles, and laugh with clean comedy — all without ever leaving the app. The Bible Dictionary, Concordance, 917 Precepts, Apocrypha, Book of Enoch, Book of Jubilees, and Book of Jasher are fully translated so deep study happens in your native tongue. One app. Every age. Every language.",
].join(" ");

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/33c49a49-4b5d-43fb-a2a2-4b27681abf6a/images/100e04a3fadaf704cc02264fad3969b261c773aa0b60913c93c05067c977e5d3.png";
const VOTD_BG = "https://static.prod-images.emergentagent.com/jobs/33c49a49-4b5d-43fb-a2a2-4b27681abf6a/images/18861c244987ba45c6f8d8e800b4072090894b5f6fd9c85439f6ef396123d43d.png";

export default function Home() {
  const { user } = useAuth();
  const [verse, setVerse] = useState(null);
  const [videos, setVideos] = useState([]);
  const [featuredRoom, setFeaturedRoom] = useState(null);
  const [lastRead, setLastRead] = useState(null);
  const [streak, setStreak] = useState(null);
  const [made, setMade] = useState(null);
  const [foundational, setFoundational] = useState([]);
  const [foundationalSermons, setFoundationalSermons] = useState([]);
  const [foundationalSongs, setFoundationalSongs] = useState([]);
  const { playList, setShuffle } = useBgMusic() || {};

  useEffect(() => {
    // Critical first-paint calls — fire immediately. These power
    // hero-banner content the user sees above the fold.
    api.get("/verse-of-the-day").then((r) => setVerse(r.data)).catch(() => {});
    api.get("/videos").then((r) => setVideos(r.data.videos.slice(0, 4))).catch(() => {});

    // Non-critical rails — defer to requestIdleCallback so they don't
    // contend with the main-thread work the first paint depends on.
    // On a cold mobile load this saves 200-400ms of perceived input
    // lag because the parse/decode of these JSON payloads moves to
    // browser idle time. Fallback to setTimeout for Safari < 17.
    const idle = (cb) =>
      typeof window !== "undefined" && "requestIdleCallback" in window
        ? window.requestIdleCallback(cb, { timeout: 1500 })
        : setTimeout(cb, 300);
    idle(() => {
      api.get("/rooms/featured").then((r) => setFeaturedRoom(r.data.room)).catch(() => {});
      api.get("/gospel-music/made-for-you").then((r) => setMade(r.data)).catch(() => {});
      // PERF: ask the backend for ONLY the 8 cards each rail renders.
      // Previously we pulled the full 341-movie / 291-sermon / 433-song
      // catalogs (~260KB combined JSON) just to .slice(0, 8) on the
      // client — that's 96% wasted bytes on every Home cold load.
      // Adding ?limit + ?voice/?sound server-side cuts the Home rail
      // payload from ~260KB to ~18KB, an order of magnitude faster
      // over 3G/4G + lower battery drain parsing on mobile.
      api.get("/movies/black-american?limit=8").then((r) => {
        setFoundational(r.data.movies || []);
      }).catch(() => {});
      api.get("/sermons?voice=foundational&limit=6").then((r) => {
        setFoundationalSermons(r.data.items || []);
      }).catch(() => {});
      api.get("/gospel-music?sound=foundational&limit=6").then((r) => {
        setFoundationalSongs(r.data.items || []);
      }).catch(() => {});
    });
  }, []);

  useEffect(() => {
    if (user && user !== false) {
      api.get("/reading/last").then((r) => {
        if (r.data?.last) setLastRead(r.data.last);
        else readLocal();
      }).catch(readLocal);
      api.get("/reading/streak").then((r) => setStreak(r.data)).catch(() => {});
    } else if (user === false) {
      readLocal();
    }
    function readLocal() {
      try {
        const raw = localStorage.getItem("lumen:last-read");
        if (raw) setLastRead(JSON.parse(raw));
      } catch {}
    }
  }, [user]);

  // Scroll-triggered entrance for the Home content blocks. Any element
  // tagged `.hw-reveal` fades + rises in the first time it scrolls into
  // view, then is unobserved so it never replays. Structural only
  // (opacity + transform via index.css) so every theme stays intact;
  // reduced-motion users get instant visibility via the CSS guard.
  useEffect(() => {
    const els = Array.from(document.querySelectorAll(".hw-reveal:not(.is-visible)"));
    if (!els.length) return;
    if (!("IntersectionObserver" in window)) {
      els.forEach((el) => el.classList.add("is-visible"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.05 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [made, foundational, foundationalSermons, foundationalSongs, featuredRoom, videos, lastRead]);

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-12 pt-10 md:pt-16 pb-10 animate-fade-up">
      <OwnerLlmHealthBanner user={user} />
      <WhatsNewTour />
      <TodayBanner slot="holidayOnly" />
      {/* One-tap morning briefing — narrates all 4 daily cards back-to-
          back via OpenAI TTS. Sits above the cards so the user doesn't
          have to scroll to start the ritual. */}
      <div className="mt-2 mb-4 flex flex-wrap items-center gap-3">
        <PlayAllTodayButton />
        <ResumeListeningChip />
      </div>
      {/* PERMANENT Daily Devotional — shows every day regardless of
          holiday status. Cannot be dismissed away (unlike the holiday
          banner above). The user explicitly asked for this after
          hitting the X on the holiday banner once and losing the
          devotional for the whole day. */}
      <div className="mb-8 hw-reveal">
        <DailyDevotionalCard />
      </div>
      {/* DAILY TRIO — On This Day · Something Good · Someone You Should
          Know. Three permanent Home-page cards that refresh every day.
          No dismiss control. User: "every day something in history
          something good something about somebody needs to be posted". */}
      <div className="mb-12 md:mb-16 hw-reveal">
        <TodayTrioCards />
      </div>
      {/* Handpicked-for-Tonight rail. Editorial 6-tile curation that
          rotates by weekday so returning visitors get an answer to
          "where do I start?" instead of staring at 340+ films and 290+
          sermons. Backed by /api/handpicked/tonight. */}
      <div className="mb-12 md:mb-16 hw-reveal">
        <HandpickedTonightRail testid="home-handpicked-tonight" />
      </div>
      {/* SUNDAY DINNER — headlining recipe section. Mounted right
          after Tonight's Pick so it's one of the first rails users
          see when they land on Home. Self-hides if no recipes are
          loaded yet, so it never shows an empty state. */}
      <div className="mb-12 md:mb-16 hw-reveal">
        <SundayDinnerRail />
      </div>
      {/* Web push opt-in removed 2026-06-30 (owner request: email only).
          Users opt into the 3 daily emails in Settings → Notifications. */}
      {/* Personalized "Your Week in Worship" card — hidden for logged-out
          users; shows wrapped-style 7-day stats with a deep link to /my-mix. */}
      <div className="mb-12 md:mb-16 hw-reveal">
        <WeekInWorshipCard />
      </div>

      {/* Late Night with Holy Words — auto-shuffled comedy queue.
          Self-hides outside 8 PM–2 AM local time. */}
      <div className="mb-12 md:mb-16 hw-reveal">
        <LateNightComedyRail />
      </div>
      {/* HERO */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-stretch hw-reveal">
        <div className="lg:col-span-7 flex flex-col justify-between">
          <div>
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-terracotta font-semibold">
              <Sparkle size={14} weight="fill" /> <Tr>Welcome to Holy Words</Tr>
            </span>
            <h1 className="font-heading text-[2.25rem] sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-forest mt-4 font-medium">
              <Tr>Faith,</Tr>{" "}
              <span className="italic text-terracotta"><Tr>but make it social.</Tr></span>
            </h1>

            {/* Home-page bio — user-provided wording (2026-02-24).
                Two paragraphs: the founder story + the medium bio,
                in the exact words the founder requested. Translated
                through <Tr eager> so the bio paints in the user's
                language on the very first frame. */}
            <div className="mt-6 max-w-2xl space-y-5 text-base md:text-lg text-muted_ink leading-relaxed">
              <p>
                <Tr eager>Holy Words started with one question: why is there no Bible app that actually built like the rest of your phone? You can post on TikTok but you can't post a 30 second testimony to your church family. You can DM your friends but you can't open a Discord-style study rooms with them and break down a chapter line by line. You can stream a billion artist but the Gospel and faith based rap you love is buried 4 playlists deep. We built the home that all of that should have had from day one.</Tr>
              </p>
              <p>
                <Tr eager>Holy Words is the first-faith social Bible app that brings Scripture to life in 18 languages with read-aloud studio voices, while doubling as the home for everything your spiritual life needs. Post videos and shorts, hop into Discord-style study rooms with friends and family, queue up gospel, faith based rap, and worship music, watch Black American faith films and foundational sermons, give the kids their own animated Bible cartoons and study group, catch the day's Live Newsroom, share Sunday Dinner recipes with the family, sharpen up on Scripture with 162 Biblical crossword and word search puzzles, and laugh with clean comedy — all without ever leaving the app. The Bible Dictionary, Concordance, 917 Precepts, Apocrypha, Book of Enoch, Book of Jubilees, and Book of Jasher are fully translated so deep study happens in your native tongue. <span className="font-heading text-forest">One app. Every age. Every language.</span></Tr>
              </p>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <ReadAloudButton
                text={HERO_BIO_SPOKEN}
                testid="hero-bio-read-aloud"
                variant="dark"
                label="Read bio aloud"
                nowPlayingLabel="Holy Words — our story"
                prefetch
                className="h-12 px-5 text-xs"
              />
              <Link
                to="/tour"
                data-testid="hero-walkthrough-tour-button"
                className="group h-12 px-5 inline-flex items-center gap-2 rounded-full bg-terracotta text-cream text-xs font-bold uppercase tracking-[0.15em] hover:bg-[#a64f31] transition-colors shadow-md"
              >
                <Play size={16} weight="fill" />
                <Tr>Take the Tour</Tr>
                <ArrowRight size={14} weight="bold" className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            {/* Action row: Continue Reading + Today's Streak + the
                two small Start Reading / Watch Stories pills, all in
                the SAME wrap-flow. The streak badge stays the visual
                anchor (largest item, terracotta gradient); the small
                pills sit alongside it so the row reads as one tight
                cluster instead of two stacked rows. */}
            <div className="mt-5 flex flex-wrap items-center gap-3 max-w-xl">
              {lastRead && (
                <Link
                  to={`/bible/${lastRead.book}/${lastRead.chapter}`}
                  data-testid="continue-reading-card"
                  className="group flex-1 min-w-[240px] inline-flex items-center gap-3 px-4 py-3 rounded-2xl bg-white border border-cream-300 hover:border-forest hover:shadow-md transition-all"
                >
                  <div className="w-10 h-10 rounded-xl bg-terracotta/10 text-terracotta grid place-items-center shrink-0">
                    <BookmarkSimple size={18} weight="duotone" />
                  </div>
                  <div className="text-left">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-muted_ink font-semibold"><Tr>Continue reading</Tr></div>
                    <div className="text-sm text-forest font-medium mt-0.5 capitalize">
                      {(lastRead.book_name || lastRead.book)} {lastRead.chapter} <span className="text-muted_ink uppercase text-xs">· {lastRead.version || "kjv"}</span>
                    </div>
                  </div>
                  <ArrowRight size={16} weight="bold" className="ml-auto text-forest group-hover:translate-x-1 transition-all" />
                </Link>
              )}
              {streak && streak.streak > 0 && (
                <div
                  data-testid="reading-streak-badge"
                  className={`inline-flex items-center gap-3 px-4 py-3 rounded-2xl border ${streak.streak >= 3 ? "bg-gradient-to-br from-terracotta to-[#a64f31] text-cream border-terracotta" : "bg-white border-cream-300 text-ink"}`}
                >
                  <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${streak.streak >= 3 ? "bg-cream/20 text-cream" : "bg-terracotta/10 text-terracotta"}`}>
                    <Flame size={20} weight="fill" />
                  </div>
                  <div>
                    <div className={`text-[10px] tracking-[0.2em] uppercase font-semibold ${streak.streak >= 3 ? "text-cream/80" : "text-muted_ink"}`}>
                      {streak.active_today ? <Tr>Today's streak</Tr> : <Tr>Keep it going</Tr>}
                    </div>
                    <div className={`text-sm font-medium mt-0.5 flex items-center gap-2 ${streak.streak >= 3 ? "text-cream" : "text-forest"}`}>
                      <span data-testid="streak-count">{streak.streak}</span> <Tr>{streak.streak > 1 ? "days" : "day"}</Tr>
                      {streak.longest_streak > streak.streak && (
                        <span className={`inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase opacity-80 ${streak.streak >= 3 ? "text-cream/80" : "text-muted_ink"}`}>
                          <Trophy size={10} weight="fill" /> <Tr>best</Tr> {streak.longest_streak}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {/* Small pills — share the same row as the streak badge
                  by request. They wrap below on narrow screens but
                  on tablet+ they sit right next to it. */}
              <Link
                to="/bible/john/3"
                data-testid="cta-start-reading"
                className="inline-flex items-center gap-1.5 px-5 h-11 rounded-full bg-forest text-cream hover:bg-forest-600 transition-all hover:scale-105 active:scale-95 text-sm font-semibold shadow-md"
              >
                <BookOpen size={14} weight="bold" /> <Tr>Start Reading</Tr>
              </Link>
              <Link
                to="/videos"
                data-testid="cta-watch-stories"
                className="inline-flex items-center gap-1.5 px-2 h-9 text-forest hover:text-terracotta transition-colors text-xs font-semibold"
              >
                <FilmStrip size={14} weight="bold" /> <Tr>Watch Stories</Tr>
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg hw-stagger">
              {[
                { n: "66", l: "Books" },
                { n: "10", l: "Languages" },
                { n: "12+", l: "Cartoon stories" },
              ].map((s) => (
                <div key={s.l} className="bg-white/60 rounded-2xl px-4 py-3 border border-cream-300 hover:-translate-y-0.5 hover:border-forest/40 hover:shadow-md transition-all">
                  <div className="font-heading text-3xl text-forest font-semibold">{s.n}</div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-muted_ink mt-1"><Tr>{s.l}</Tr></div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-5 relative rounded-[2rem] overflow-hidden min-h-[420px] lg:min-h-full bg-forest shadow-xl">
          <img src={HERO_IMG} alt="Open Bible bathed in golden light" className="absolute inset-0 w-full h-full object-cover opacity-90" />
          <div className="absolute inset-0 bg-gradient-to-t from-forest/80 via-forest/10 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-7 text-cream">
            <span className="text-[10px] tracking-[0.25em] uppercase text-cream/80"><Tr>Today's reading</Tr></span>
            <p className="font-heading text-3xl mt-2 leading-tight"><Tr>"In the beginning was the Word"</Tr></p>
            <p className="text-sm text-cream/80 mt-1">John 1:1 · KJV</p>
          </div>
        </div>
      </section>

      {/* LIVE NOW — people currently broadcasting */}
      <LiveNowRail />
      <BirthdayRail />

      {/* WEEKLY RHYTHM — themed day of the week (Mercy Monday, etc.) */}
      <section className="mt-10 md:mt-14" data-testid="home-weekly-rhythm">
        <WeeklyRhythmCard />
      </section>

      {/* CONTINUE WATCHING — surfaces user's last 6-8 played items */}
      <ContinueWatchingRail />

      {/* SUNDAY SERVICE — Saturday evening + Sunday morning banner */}
      <SundayServiceBanner />

      {/* TICKETS COLLECTION — Concerts + Clean Comedy, sits after the
          Sunday Service banner per founder request 2026-02-25. */}
      <TicketsCollection />

      {/* MOST WATCHED THIS WEEK — global trending across all buckets */}
      <MostWatchedRail />

      {/* CHANNELS YOU MIGHT LOVE — discover creators */}
      <ChannelTrendingRail />
      <ChannelSuggestionsRail />


      {/* MADE FOR YOU — daily rotating gospel playlist */}
      {made && made.items?.length > 0 && (
        <section className="mt-12 md:mt-16 hw-reveal" data-testid="home-made-for-you">
          <div
            className="relative overflow-hidden rounded-3xl p-6 md:p-8 text-cream shadow-xl"
            style={{ background: "linear-gradient(135deg, #1a3a2a 0%, #2d5d44 50%, #c75d3a 100%)" }}
          >
            <div className="absolute -right-12 -top-12 w-64 h-64 rounded-full bg-cream/5 blur-3xl" />
            <div className="absolute -left-12 -bottom-12 w-48 h-48 rounded-full bg-terracotta/20 blur-3xl" />
            <div className="relative flex flex-col md:flex-row md:items-end justify-between gap-5">
              <div className="max-w-2xl">
                <span className="text-[10px] tracking-[0.25em] uppercase font-semibold text-cream/80">Made for you · {made.tag}</span>
                <h2 className="font-heading text-3xl md:text-5xl font-medium mt-2 leading-tight">{made.theme}</h2>
                <p className="text-cream/85 text-sm md:text-base mt-3">{made.blurb}</p>
                <div className="flex flex-wrap gap-2 mt-5">
                  <button
                    onClick={() => { setShuffle?.(false); playList?.(made.items, 0); }}
                    data-testid="home-made-play"
                    className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-cream text-forest hover:bg-white transition-all hover:scale-105 text-sm font-semibold"
                  >
                    <Play size={16} weight="fill" /> Play today's mix
                  </button>
                  <button
                    onClick={() => { setShuffle?.(true); playList?.(made.items, Math.floor(Math.random() * made.items.length)); }}
                    data-testid="home-made-shuffle"
                    className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-cream/20 text-cream hover:bg-cream/30 transition-all text-sm border border-cream/30"
                  >
                    <Shuffle size={14} weight="bold" /> Shuffle
                  </button>
                  <Link
                    to="/gospel-music"
                    data-testid="home-made-browse"
                    className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-transparent text-cream/80 hover:text-cream text-sm"
                  >
                    Browse all gospel <ArrowRight size={14} weight="bold" />
                  </Link>
                </div>
              </div>
            </div>
            <div className="relative mt-6 flex gap-3 overflow-x-auto pb-2 -mx-2 px-2" data-testid="home-made-strip">
              {made.items.slice(0, 8).map((v, i) => (
                <button
                  key={v.id}
                  onClick={() => { setShuffle?.(false); playList?.(made.items, i); }}
                  data-testid={`home-made-track-${v.id}`}
                  className="group shrink-0 w-32 text-left"
                  title={`${v.title} — ${v.artist}`}
                >
                  <div className="aspect-video relative rounded-xl overflow-hidden bg-forest-700">
                    <MediaThumb youtube_id={v.youtube_id} title={v.title} subtitle={v.artist} kind="music" />
                    <div className="absolute inset-0 bg-black/35 group-hover:bg-black/15 transition-colors" />
                    <div className="absolute inset-0 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Play size={20} weight="fill" className="text-cream drop-shadow-lg" />
                    </div>
                  </div>
                  <div className="text-[11px] mt-1.5 text-cream truncate font-semibold">{v.title}</div>
                  <div className="text-[10px] text-cream/65 truncate">{v.artist}</div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PICTURE BIBLE — AI storybook quick-link */}
      <section className="mt-8 hw-reveal" data-testid="home-storybook-tile">
        <Link
          to="/storybook"
          className="group block relative overflow-hidden rounded-3xl border border-cream-300 bg-white hover:shadow-xl hover:-translate-y-0.5 transition-all p-6 md:p-8"
        >
          <div className="flex flex-col md:flex-row md:items-center gap-5 justify-between">
            <div>
              <span className="text-[10px] tracking-[0.25em] uppercase text-terracotta font-semibold flex items-center gap-2">
                <Sparkle size={14} weight="fill" /> AI Bible Storybook · New
              </span>
              <h3 className="font-heading text-2xl md:text-3xl text-forest mt-2">Picture Bible for little ones</h3>
              <p className="text-sm text-muted_ink mt-2 max-w-2xl">25 illustrated, narrated Bible stories for ages 4–9. Tap any story and we'll paint and read it for you.</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="inline-flex items-center gap-2 px-5 h-11 text-forest hover:text-terracotta group-hover:bg-forest-600 transition-all text-sm font-medium">
                Open storybook <ArrowRight size={14} weight="bold" />
              </span>
            </div>
          </div>
        </Link>
      </section>

      {/* FOUNDATIONAL HUB — Films + Sermons + Music in one section */}
      {(foundational.length > 0 || foundationalSermons.length > 0 || foundationalSongs.length > 0) && (
        <section className="mt-8 md:mt-12 hw-reveal" data-testid="home-foundational-hub">
          <div
            className="relative overflow-hidden rounded-2xl p-4 md:p-5 text-cream shadow-lg"
            style={{ background: "linear-gradient(135deg, #2a1810 0%, #5c2818 45%, #c75d3a 105%)" }}
          >
            <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-cream/5 blur-3xl" />
            <div className="absolute -left-8 -bottom-12 w-32 h-32 rounded-full bg-amber-400/15 blur-3xl" />
            <div className="relative">
              <div className="flex items-end justify-between gap-3 mb-4">
                <div className="min-w-0">
                  <span className="text-[9px] tracking-[0.25em] uppercase font-semibold text-amber-200">Featured collection</span>
                  <h2 className="font-heading text-lg md:text-xl mt-0.5">The Foundational Collection</h2>
                  <p className="text-cream/80 text-xs mt-1 max-w-xl">
                    Honoring the Black American church tradition — feature films, sermons from the great Black pulpit, and the songs that built gospel music.
                  </p>
                </div>
              </div>

              {/* Films sub-rail */}
              {foundational.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-1.5">
                    <h3 className="text-[10px] tracking-[0.25em] uppercase font-semibold text-amber-200">Foundational Faith · Black American Films</h3>
                    <Link to="/movies/black-american" data-testid="home-foundational-films-cta" className="text-[10px] uppercase tracking-wider text-cream/80 hover:text-white flex items-center gap-1">
                      See all <ArrowRight size={10} weight="bold" />
                    </Link>
                  </div>
                  <div className="flex gap-2.5 overflow-x-auto pb-1.5 -mx-1 px-1" data-testid="home-foundational-films-strip">
                    {foundational.map((m) => (
                      <Link
                        key={m.id}
                        to={`/movies/black-american?play=${encodeURIComponent(m.id)}`}
                        data-testid={`home-foundational-film-${m.id}`}
                        className="group shrink-0 w-36 md:w-40 text-left"
                        title={m.title}
                        onMouseDown={() => { try { window.dispatchEvent(new Event("hw:audio:start")); } catch (_) {} }}
                        onTouchStart={() => { try { window.dispatchEvent(new Event("hw:audio:start")); } catch (_) {} }}
                      >
                        <div className="aspect-video relative rounded-xl overflow-hidden bg-forest-700">
                          <MediaThumb youtube_id={m.youtube_id} poster_url={m.poster} title={m.title} subtitle={m.director || m.year} kind="story" />
                          <div className="absolute inset-0 bg-black/35 group-hover:bg-black/15 transition-colors" />
                          <div className="absolute inset-0 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <Play size={20} weight="fill" className="text-cream drop-shadow-lg" />
                          </div>
                        </div>
                        <div className="text-[11px] mt-1.5 text-cream truncate font-semibold">{m.title}</div>
                        <div className="text-[10px] text-cream/65 truncate">{m.director || m.year}</div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Sermons sub-rail */}
              {foundationalSermons.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-1.5">
                    <h3 className="text-[10px] tracking-[0.25em] uppercase font-semibold text-amber-200">Foundational Voices · Sermons</h3>
                    <Link to="/sermons" data-testid="home-foundational-sermons-cta" className="text-[10px] uppercase tracking-wider text-cream/80 hover:text-white flex items-center gap-1">
                      See all <ArrowRight size={10} weight="bold" />
                    </Link>
                  </div>
                  <div className="flex gap-2.5 overflow-x-auto pb-1.5 -mx-1 px-1" data-testid="home-foundational-sermons-strip">
                    {foundationalSermons.map((s) => (
                      <Link
                        key={s.id}
                        to={`/sermons?play=${encodeURIComponent(s.id)}`}
                        data-testid={`home-foundational-sermon-${s.id}`}
                        className="group shrink-0 w-36 md:w-40 text-left"
                        title={s.title}
                        onMouseDown={() => { try { window.dispatchEvent(new Event("hw:audio:start")); } catch (_) { /* noop */ } }}
                        onTouchStart={() => { try { window.dispatchEvent(new Event("hw:audio:start")); } catch (_) { /* noop */ } }}
                      >
                        <div className="aspect-video relative rounded-xl overflow-hidden bg-forest-700">
                          <MediaThumb youtube_id={s.youtube_id} title={s.title} subtitle={s.speaker} kind="sermon" />
                          <div className="absolute inset-0 bg-black/35 group-hover:bg-black/15 transition-colors" />
                          <div className="absolute inset-0 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <Play size={20} weight="fill" className="text-cream drop-shadow-lg" />
                          </div>
                        </div>
                        <div className="text-[11px] mt-1.5 text-cream truncate font-semibold">{s.title}</div>
                        <div className="text-[10px] text-cream/65 truncate">{s.speaker}</div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Music sub-rail */}
              {foundationalSongs.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <h3 className="text-[10px] tracking-[0.25em] uppercase font-semibold text-amber-200">Foundational Sound · Music</h3>
                    <Link to="/gospel-music" data-testid="home-foundational-music-cta" className="text-[10px] uppercase tracking-wider text-cream/80 hover:text-white flex items-center gap-1">
                      See all <ArrowRight size={10} weight="bold" />
                    </Link>
                  </div>
                  <div className="flex gap-2.5 overflow-x-auto pb-1.5 -mx-1 px-1" data-testid="home-foundational-music-strip">
                    {foundationalSongs.map((t) => (
                      <Link
                        key={t.id}
                        to={`/gospel-music?play=${encodeURIComponent(t.id)}`}
                        data-testid={`home-foundational-song-${t.id}`}
                        className="group shrink-0 w-36 md:w-40 text-left"
                        title={t.title}
                        onMouseDown={() => { try { window.dispatchEvent(new Event("hw:audio:start")); } catch (_) { /* noop */ } }}
                        onTouchStart={() => { try { window.dispatchEvent(new Event("hw:audio:start")); } catch (_) { /* noop */ } }}
                      >
                        <div className="aspect-video relative rounded-xl overflow-hidden bg-forest-700">
                          <MediaThumb youtube_id={t.youtube_id} title={t.title} subtitle={t.artist} kind="music" />
                          <div className="absolute inset-0 bg-black/35 group-hover:bg-black/15 transition-colors" />
                          <div className="absolute inset-0 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <Play size={20} weight="fill" className="text-cream drop-shadow-lg" />
                          </div>
                        </div>
                        <div className="text-[11px] mt-1.5 text-cream truncate font-semibold">{t.title}</div>
                        <div className="text-[10px] text-cream/65 truncate">{t.artist}</div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* DAILY READING PLAN — AI-powered */}
      <section className="mt-12 md:mt-16">
        <DailyPlan />
      </section>

      {/* VERSE OF DAY */}
      <section className="mt-16 md:mt-24 relative rounded-[2rem] overflow-hidden hw-reveal">
        <img src={VOTD_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-forest/95 via-forest/85 to-forest/60" />
        <div className="relative px-7 md:px-14 py-12 md:py-20 max-w-4xl">
          <span className="text-[10px] tracking-[0.3em] uppercase text-terracotta font-semibold">Verse of the day</span>
          <p className="font-heading text-3xl md:text-5xl text-cream mt-4 leading-[1.2] italic" data-testid="verse-of-day-text">
            {verse?.text ? `"${verse.text}"` : "Loading today's verse..."}
          </p>
          <p className="text-cream/70 mt-4 text-sm tracking-wider" data-testid="verse-of-day-reference">
            — {verse?.reference || "—"} · {verse?.translation || "KJV"}
          </p>
          {verse?.reference && (
            <Link
              to={`/read?ref=${encodeURIComponent(verse.reference)}`}
              data-testid="verse-of-day-cta"
              className="mt-6 inline-flex items-center gap-2 px-5 h-11 text-terracotta hover:text-terracotta/80 hover:bg-terracotta-600 transition-all hover:scale-105 text-sm font-medium"
            >
              Open · Listen · Share <ArrowRight size={14} weight="bold" />
            </Link>
          )}
        </div>
      </section>

      {/* TONIGHT'S PRAYER — Lord's Prayer with rotating daily intention */}
      <TonightPrayer />

      {/* DAILY DEVOTIONAL — email opt-in card. Highest-leverage growth
          surface: daily inbox touch with a one-tap CTA back into the
          app. Lives high on the homepage but below TonightPrayer so
          the first-fold experience stays content-first, not signup-first. */}
      <section className="mt-12 md:mt-20" data-testid="daily-devotional-section">
        <DailyDevotionalSubscribe />
      </section>

      {/* FEATURED ROOM */}
      {featuredRoom && (
        <section className="mt-16 md:mt-24 hw-reveal">
          <div className="flex items-end justify-between mb-6 gap-4">
            <div>
              <span className="text-xs tracking-[0.2em] uppercase text-terracotta font-semibold">Most active this week</span>
              <h2 className="font-heading text-3xl md:text-5xl text-forest mt-2 font-medium">Featured room</h2>
            </div>
            <Link to="/rooms" data-testid="see-all-rooms" className="text-sm text-forest hover:text-terracotta flex items-center gap-1.5">
              All rooms <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
          <Link
            to={`/rooms/${featuredRoom.id}`}
            data-testid="featured-room-card"
            className="group block bg-white border border-cream-300 rounded-3xl p-7 md:p-9 hover:border-forest hover:shadow-xl transition-all"
          >
            <div className="flex flex-wrap gap-3 items-center">
              <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase text-terracotta font-semibold bg-terracotta/10 px-3 py-1 rounded-full">
                <Hash size={11} weight="bold" /> {featuredRoom.category}
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs text-muted_ink"><Users size={12} weight="bold" /> {featuredRoom.member_count} members</span>
              <span className="inline-flex items-center gap-1.5 text-xs text-muted_ink"><ChatCircle size={12} weight="bold" /> {featuredRoom.message_count} messages</span>
            </div>
            <h3 className="font-heading text-3xl md:text-5xl text-forest mt-4 leading-tight" data-testid="featured-room-name">{featuredRoom.name}</h3>
            {featuredRoom.description && (
              <p className="text-muted_ink mt-3 text-base md:text-lg max-w-2xl">{featuredRoom.description}</p>
            )}
            {featuredRoom.last_message_preview && (
              <p className="font-heading italic text-ink/70 mt-5 text-lg line-clamp-2 max-w-2xl">"{featuredRoom.last_message_preview}"</p>
            )}
            <div className="mt-7 inline-flex items-center gap-2 text-sm text-forest group-hover:text-terracotta transition-all font-medium">
              Step inside <ArrowRight size={16} weight="bold" className="group-hover:translate-x-1 transition-all" />
            </div>
          </Link>
        </section>
      )}

      {/* Foundational Black-American canon rail — moved here (was at
          the very top of the page) per user request 2026-05-22: "I
          just want the foundational black American stuff after the
          church stuff". Featured Room above is the "church stuff". */}
      <section className="mt-12 md:mt-16" data-testid="home-black-canon-after-church">
        <TodayBanner slot="canonOnly" />
      </section>

      {/* FEATURED VIDEOS */}
      <section className="mt-16 md:mt-24 hw-reveal">
        <div className="flex items-end justify-between mb-8 gap-4">
          <div>
            <span className="text-xs tracking-[0.2em] uppercase text-terracotta font-semibold">For little hearts</span>
            <h2 className="font-heading text-3xl md:text-5xl text-forest mt-2 font-medium">Animated stories</h2>
          </div>
          <Link to="/videos" data-testid="see-all-videos" className="text-sm text-forest hover:text-terracotta flex items-center gap-1.5">
            See all <ArrowRight size={14} weight="bold" />
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 hw-stagger">
          {videos.map((v) => (
            <Link
              key={v.id}
              to={`/videos?play=${v.id}`}
              data-testid={`featured-video-${v.id}`}
              className="group relative rounded-xl overflow-hidden border border-cream-300 bg-white hover:shadow-lg hover:-translate-y-1 transition-all"
            >
              <div className="aspect-video bg-forest/90 relative overflow-hidden">
                <img
                  src={`https://i.ytimg.com/vi/${v.youtube_id}/hqdefault.jpg`}
                  onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1576662712957-9c79ae1280f8?w=800&q=70"; }}
                  alt={v.title}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              </div>
              <div className="p-2.5">
                <div className="text-[9px] tracking-[0.2em] uppercase text-terracotta">{v.category}</div>
                <h3 className="font-heading text-sm text-forest mt-0.5 leading-snug line-clamp-2">{v.title}</h3>
                <p className="text-[10px] text-muted_ink mt-0.5 line-clamp-1">{v.story} · {v.duration}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CROSS-DEVICE — nudge installs across phone / tablet / Chromebook /
          desktop now that Holy Words is live on Google Play. */}
      <CrossDeviceBanner />

      {/* SITE FOOTER CARD — the gray "Faith, but make it social."
          card with the short blurb, founder credit (Mr Givon Johnson),
          and legal links. Mounted ONLY on the home page, just below
          the "Animated stories" / Tower of Babel section, per the
          founder's request. Not rendered globally on other routes. */}
      <SiteFooter />
    </div>
  );
}
